import { Check } from 'lucide-react'
import {
  isValidElement,
  useCallback,
  useId,
  useState,
  type Key,
  type KeyboardEvent,
  type ReactNode,
  type WheelEvent,
} from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

const readOptionId = (option: unknown): unknown =>
  typeof option === 'object' && option !== null && 'id' in option ? option.id : undefined

const readOptionValue = (option: unknown): unknown =>
  typeof option === 'object' && option !== null && 'value' in option ? option.value : undefined

const readOptionName = (option: unknown): unknown =>
  typeof option === 'object' && option !== null && 'name' in option ? option.name : undefined

const readOptionLabel = (option: unknown): unknown =>
  typeof option === 'object' && option !== null && 'label' in option ? option.label : undefined

// Typed `unknown` on purpose: String() is well defined for every value.
const stringify = (value: unknown): string => String(value)

// React coerces every key to a string, so stringifying a non-primitive key keeps the same identity.
const toOptionKey = (value: unknown): Key | undefined => {
  if (value === null || typeof value === 'undefined') return undefined
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') {
    return value
  }
  return stringify(value)
}

const toOptionLabel = (value: unknown): ReactNode => {
  if (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'bigint' ||
    typeof value === 'boolean' ||
    isValidElement(value)
  ) {
    return value
  }
  return undefined
}

const defaultGetOptionKey = (option: unknown, index: number): Key =>
  toOptionKey(readOptionId(option)) ?? toOptionKey(readOptionValue(option)) ?? index
const defaultGetOptionLabel = (option: unknown): ReactNode =>
  toOptionLabel(readOptionName(option)) ??
  toOptionLabel(readOptionLabel(option)) ??
  stringify(option ?? '')

function clampOptionIndex(index: number | null, optionCount: number): number | null {
  if (optionCount === 0) return null
  return Math.max(0, Math.min(index ?? 0, optionCount - 1))
}

export type UseComboboxListboxOptions = {
  id?: string
  optionCount: number
  onClose?: () => void
}

export type ComboboxKeyDownHandler = (
  event: KeyboardEvent<HTMLInputElement>,
  onSelect?: (index: number) => void
) => void

export type UseComboboxListboxResult = {
  activateOption: (index: number) => void
  activeIndex: number | null
  activeOptionId: string | undefined
  close: () => void
  handleKeyDown: ComboboxKeyDownHandler
  isOpen: boolean
  listboxId: string
  openWithFirstOption: () => void
  openWithoutActiveOption: () => void
}

export function useComboboxListbox({
  id,
  optionCount,
  onClose,
}: UseComboboxListboxOptions): UseComboboxListboxResult {
  const generatedId = useId()
  const listboxId = id ?? generatedId
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndexState, setActiveIndex] = useState<number | null>(null)
  const activeIndex = isOpen ? clampOptionIndex(activeIndexState, optionCount) : null
  const activeOptionId = activeIndex === null ? undefined : `${listboxId}-option-${activeIndex}`

  const close = useCallback(() => {
    setIsOpen(false)
    setActiveIndex(null)
    onClose?.()
  }, [onClose])

  const openWithFirstOption = useCallback(() => {
    setIsOpen(true)
    setActiveIndex(optionCount > 0 ? 0 : null)
  }, [optionCount])

  const openWithoutActiveOption = useCallback(() => {
    setIsOpen(true)
    setActiveIndex(null)
  }, [])

  const scrollOptionIntoView = useCallback(
    (index: number) => {
      const listbox = document.getElementById(listboxId)
      const option = document.getElementById(`${listboxId}-option-${index}`)

      if (!listbox || !option) return

      const listboxRect = listbox.getBoundingClientRect()
      const optionRect = option.getBoundingClientRect()

      if (optionRect.top < listboxRect.top) {
        listbox.scrollTop -= listboxRect.top - optionRect.top
      } else if (optionRect.bottom > listboxRect.bottom) {
        listbox.scrollTop += optionRect.bottom - listboxRect.bottom
      }
    },
    [listboxId]
  )

  const activateOption = useCallback(
    (index: number) => {
      const nextIndex = clampOptionIndex(index, optionCount)
      setActiveIndex(nextIndex)

      if (nextIndex !== null) scrollOptionIntoView(nextIndex)
    },
    [optionCount, scrollOptionIntoView]
  )

  const handleKeyDown = useCallback<ComboboxKeyDownHandler>(
    (event, onSelect) => {
      if (event.key === 'Escape' && isOpen) {
        event.preventDefault()
        close()
        return
      }

      if (event.key === 'Enter') {
        if (isOpen && activeIndex !== null) {
          event.preventDefault()
          onSelect?.(activeIndex)
        } else if (String(event.currentTarget?.value ?? '').trim() !== '') {
          event.preventDefault()
        }
        return
      }

      if (optionCount === 0) return

      if (event.key === 'ArrowDown') {
        event.preventDefault()

        if (!isOpen) {
          openWithFirstOption()
          return
        }

        activateOption((activeIndex ?? -1) + 1)
        return
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault()

        if (!isOpen) {
          setIsOpen(true)
          activateOption(optionCount - 1)
          return
        }

        activateOption(activeIndex === null ? optionCount - 1 : activeIndex - 1)
        return
      }

      if (event.key === 'Home' && isOpen) {
        event.preventDefault()
        activateOption(0)
        return
      }

      if (event.key === 'End' && isOpen) {
        event.preventDefault()
        activateOption(optionCount - 1)
      }
    },
    [activateOption, activeIndex, close, isOpen, openWithFirstOption, optionCount]
  )

  return {
    activateOption,
    activeIndex,
    activeOptionId,
    close,
    handleKeyDown,
    isOpen,
    listboxId,
    openWithFirstOption,
    openWithoutActiveOption,
  }
}

function handleContainedWheel(event: WheelEvent<HTMLDivElement>) {
  const listbox = event.currentTarget

  if (event.deltaY === 0) return

  const maxScrollTop = Math.max(0, listbox.scrollHeight - listbox.clientHeight)
  if (maxScrollTop === 0) return

  const nextScrollTop = Math.max(0, Math.min(maxScrollTop, listbox.scrollTop + event.deltaY))
  if (nextScrollTop === listbox.scrollTop) return

  event.preventDefault()
  event.stopPropagation()
  listbox.scrollTop = nextScrollTop
}

export type ComboboxListboxProps<TOption> = {
  activeIndex: number | null
  className?: string
  containWheel?: boolean
  emptyMessage?: ReactNode
  getOptionKey?: (option: TOption, index: number) => Key
  getOptionLabel?: (option: TOption) => ReactNode
  isLoading?: boolean
  items: readonly TOption[]
  listboxId: string
  onActivateOption: (index: number) => void
  onSelectOption: (option: TOption) => void
  renderOption?: (option: TOption) => ReactNode
  selectedOptionKey?: Key | null
  showSelectionIndicator?: boolean
}

export function ComboboxListbox<TOption>({
  activeIndex,
  className,
  containWheel = false,
  emptyMessage,
  getOptionKey = defaultGetOptionKey,
  getOptionLabel = defaultGetOptionLabel,
  isLoading = false,
  items,
  listboxId,
  onActivateOption,
  onSelectOption,
  renderOption,
  selectedOptionKey,
  showSelectionIndicator = false,
}: ComboboxListboxProps<TOption>) {
  return (
    <div
      id={listboxId}
      role="listbox"
      data-autocomplete-options-root="true"
      className={cn(
        'pretty-scrollbar max-h-[300px] w-full overflow-x-hidden overflow-y-auto overscroll-contain p-1 text-foreground',
        className
      )}
      onWheel={containWheel ? handleContainedWheel : undefined}
    >
      {isLoading ? (
        <div className="p-1" role="status" aria-label="Loading suggestions">
          <Skeleton className="h-8 w-full" />
        </div>
      ) : null}
      {!isLoading && items.length === 0 && emptyMessage ? (
        <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
      ) : null}
      {!isLoading
        ? items.map((option, index) => {
            const optionKey = getOptionKey(option, index)
            const isSelected = selectedOptionKey === optionKey

            return (
              <button
                id={`${listboxId}-option-${index}`}
                key={optionKey}
                type="button"
                data-raw-button-owner="combobox-option"
                role="option"
                aria-selected={activeIndex === index}
                tabIndex={-1}
                className="relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-hidden hover:bg-accentSecondary hover:text-accentSecondary-foreground aria-selected:bg-accentSecondary aria-selected:text-accentSecondary-foreground"
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => onActivateOption(index)}
                onClick={() => onSelectOption(option)}
              >
                {showSelectionIndicator ? (
                  <Check
                    className={cn('size-4 shrink-0', isSelected ? 'opacity-100' : 'opacity-0')}
                  />
                ) : null}
                <div className="min-w-0 flex-1">
                  {renderOption ? renderOption(option) : getOptionLabel(option)}
                </div>
              </button>
            )
          })
        : null}
    </div>
  )
}
